import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";

export const useTravelStore = defineStore("travels", () => {
  const travels = ref([]);
  const meta = ref(null);
  const isLoading = ref(false);
  const isExporting = ref(false);
  const travelStatsByStatus = ref(null);

  const fetchTravels = async (parameters = {}) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const params = {
        page: 1,
        ...parameters,
      };

      const response = await axios.get("/api/travels", { params });
      travels.value = response.data.data;
      meta.value = response.data.meta;
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to fetch travels";
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTravelById = async (id) => {
    try {
      const response = await axios.get(`/api/travels/${id}`);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to get travel";
    } finally {
      isLoading.value = false;
    }
  };

  const addTravel = async (travelData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.post("/api/travels", travelData);
      travels.value.push(response.data.data);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to add travel";
    } finally {
      isLoading.value = false;
    }
  };

  const updateTravel = async (id, travelData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(`/api/travels/${id}`, travelData);
      const index = travels.value.findIndex((travel) => travel.id === id);
      if (index !== -1) {
        travels.value[index] = response.data.data;
      }
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to update travel";
    } finally {
      isLoading.value = false;
    }
  };

  const reassignDriverVehicle = async (id, payload) => {
    if (isLoading.value) return;

    isLoading.value = true;

    // Unlike sibling actions in this file, this does not catch/flatten the
    // error — it lets the raw error propagate so the modal can read
    // err.response.data.errors / .message per frontend/CLAUDE.md's
    // form-validation convention, needed to distinguish the 422 "not
    // travelStatus1" message from the cross-org validation error.
    try {
      const response = await axios.put(
        `/api/travels/${id}/reassign-driver-vehicle`,
        payload,
      );
      return response.data.data;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteTravel = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      await axios.delete(`/api/travels/${id}`);
      travels.value = travels.value.filter((travel) => travel.id !== id);
      return true;
    } catch (err) {
      throw err.response?.data?.message || "Failed to delete travel";
    } finally {
      isLoading.value = false;
    }
  };

  const bulkCopy = async (params) => {
    try {
      const response = await axios.post("/api/travels/bulk-copy", params);
      console.log(response);
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to creat travel copies";
    }
  };

  const checkAvailability = async (params) => {
    try {
      const response = await axios.get("/api/travels/check-availability", {
        params: {
          date: params.date,
          passengersRegularCount: params.passengersRegularCount,
          passengersDisabledCount: params.passengersDisabledCount,
          startTime: params.startTime,
          finishTime: params.finishTime,
        },
      });
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to check date availability";
    }
  };

  const checkTravelsOverlap = async (params) => {
    try {
      const response = await axios.get("/api/travels/check-overlap", {
        params,
      });
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to check travels overlap";
    }
  };

  const checkTripAddAvailability = async (existingWaypoints, newWaypoints) => {
    if (isLoading.value) return;
    isLoading.value = true;

    try {
      const response = await axios.post(
        "/api/travels/check-trip-add-availability",
        {
          existing_waypoints: existingWaypoints,
          new_waypoints: newWaypoints,
        },
      );
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to check trip availability";
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTravelStatsByStatus = async () => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.get("/api/travels/get-stat-by-status");
      travelStatsByStatus.value = response.data.data;
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to fetch travel statistics";
    } finally {
      isLoading.value = false;
    }
  };

  // Start travel
  const startTravel = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(`/api/travels/${id}/start`);
      const index = travels.value.findIndex((travel) => travel.id === id);
      if (index !== -1) {
        travels.value[index] = response.data.data;
      }
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to start travel";
    } finally {
      isLoading.value = false;
    }
  };

  // Start travel
  const finishTravel = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(`/api/travels/${id}/finish`);
      const index = travels.value.findIndex((travel) => travel.id === id);
      if (index !== -1) {
        travels.value[index] = response.data.data;
      }
      return response.data.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to finish travel";
      console.error("Travel finish error:", err);
      throw new Error(errorMessage); // Create a proper Error object
    } finally {
      isLoading.value = false;
    }
  };

  // Export travels
  const exportTravels = async (parameters = {}) => {
    if (isExporting.value) return;
    isExporting.value = true;

    try {
      const response = await axios.get("/api/travels/export", {
        params: parameters,
        responseType: "blob",
      });

      // Define filename directly in the frontend
      const currentDate = new Date().toISOString().slice(0, 10);
      const fileName = `travels_export_${currentDate}.xlsx`;

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting travels:", err);
    } finally {
      isExporting.value = false;
    }
  };

  return {
    travels,
    meta,
    isLoading,
    travelStatsByStatus,
    fetchTravels,
    fetchTravelById,
    addTravel,
    updateTravel,
    reassignDriverVehicle,
    deleteTravel,
    bulkCopy,
    checkAvailability,
    checkTripAddAvailability,
    checkTravelsOverlap,
    fetchTravelStatsByStatus,
    startTravel,
    finishTravel,
    isExporting,
    exportTravels,
  };
});
